import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Footer as OKRCreationFooter } from 'components/RabbitOKRCreationPanel/OKRCreationPanelFooter';
import { MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON } from 'components/RabbitOKRSidePanel/constants';
import {
  SECONDARY_PANEL_TYPES,
  openOtherDetailsPanel,
  openTitleAndMetricPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import { AddKeyresultsPlus, AddObjectivePlus } from 'assets/assetsV1/iconComponents';

import { OBJECTIVE_TYPES_MAP, OKR_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import {
  sendAIButtonClickedEvent,
  sendChangeDetailsClickedEvent,
  sendClickedOnHelpMeWriteEvent,
} from 'constants/mixpanelHelper';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import MavenButton from 'uiToolkitV1/MavenButton/MavenButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { Alignment } from './sections/Alignment';
import { OtherDetails } from './sections/OtherDetails';
import { OwnersSection } from './sections/OwnersSection';
import { PrimaryDetails } from './sections/PrimaryDetails';
import { TrackingSection } from './sections/TrackingSection';
import { setInitialDetails } from './slice/okrDrawerChangeDetailsSlice';

interface Props {
  isOpenAsParent?: boolean;
  showSecondaryPanel?: boolean;
  onKeyResultCreation?: () => void;
  onObjectiveCreation?: () => void;
  onClose?: () => void;
  isSecondaryPanel?: boolean;
  isAlignmentDisabled?: boolean;
}

const HeaderWithIcon = ({ isObjective }) => {
  const Icon = isObjective ? AddObjectivePlus : AddKeyresultsPlus;
  const { objectivesName, keyResultsName } = useOrganisationSettings();

  return (
    <Flex alignItems="center">
      <Icon boxSize="24px" color="text.g2" mr="8px" />
      <TextBodyMedium color="text.g1" bold fontSize="16px" lineHeight="24px">
        Add{' '}
        {isObjective
          ? modifyString(objectivesName, StringModifier.TITLE_CASE)
          : modifyString(keyResultsName, StringModifier.TITLE_CASE)}
      </TextBodyMedium>
    </Flex>
  );
};

const OKRCreationPrimaryPanelV2 = ({
  onClose = () => {},
  showSecondaryPanel = false,
  onObjectiveCreation = () => {},
  onKeyResultCreation = () => {},
  isSecondaryPanel = false,
  isAlignmentDisabled = false,
}: Props) => {
  const dispatch = useDispatch();
  const { moduleName } = useOrganisationSettings();

  const secondaryPanelDetailsObject = useSelector(kRDrawerSecondaryPanelSelector);

  const okrDetails = useSelector(okrCreationPanelStateSelector);
  const { type, text } = okrDetails;

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const { OBJECTIVE_TYPE } = useOKRConfig();

  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const showTrackingSection = !isObjective || isDetailedObjectiveType;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;

  const showOwnersAndCollaborators = !isObjective || isCompactObjectiveType || isDetailedObjectiveType;

  const showDates = !isObjective || isDetailedObjectiveType;

  const isMeshMavenButtonDisabled =
    !okrDetails?.parentPost?.uuid && text.replaceAll(' ', '').length < MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON;

  const changeDetailsHandler = () => {
    sendChangeDetailsClickedEvent({
      eventProperties: {
        goalId: 'NA',
        source: 'Change Details Button',
        type: GOALS_EVENT_SOURCES.ADD_GOAL_PANEL,
      },
    });
    dispatch(openOtherDetailsPanel({ isOpenAsChild: isSecondaryPanel, okrDetails }));
    dispatch(
      setInitialDetails({
        details: okrDetails,
        isOpenAsChild: isSecondaryPanel,
      })
    );
  };

  return (
    // @ts-ignore
    <Flex direction="column" overflow="hidden" height="100%" backgroundColor="white" width="100%">
      {!isSecondaryPanel && (
        <DrawerHeader onClose={onClose} opacity={1} showCloseButton={false}>
          <Box
            p="12px"
            pb="8px !important"
            opacity={showSecondaryPanel ? 0.5 : 1}
            pointerEvents={showSecondaryPanel ? 'none' : 'unset'}
          >
            <HeaderWithIcon isObjective={isObjective} />
          </Box>
        </DrawerHeader>
      )}
      <Flex
        flexDirection="column"
        width="100%"
        flexGrow={1}
        height="calc(100% - 133px)"
        overflow="scroll"
        padding="8px"
        borderRadius="8px"
      >
        <Flex flexDirection="column" padding="12px 16px" bg="#FDFDFD" border="1px solid #EBEFF3" borderRadius="8px">
          <PrimaryDetails isSecondaryPanel={isSecondaryPanel} />
          {showTrackingSection && <TrackingSection />}
          {/* @ts-ignore */}
          <Alignment isAlignmentDisabled={isAlignmentDisabled} />
          {showOwnersAndCollaborators && <OwnersSection />}
          {checkGoalGenAIEnabled() && (
            <Flex justifyContent="flex-end" p="4px" mt="8px">
              <Tooltip
                label={`For AI assistance, set a parent ${modifyString(
                  moduleName,
                  StringModifier.TITLE_CASE
                )} or use a title with at least 10 characters.`}
                isDisabled={!isMeshMavenButtonDisabled}
              >
                <MavenButton
                  // @ts-ignore
                  text="Help me write"
                  onClick={() => {
                    sendAIButtonClickedEvent({
                      eventProperties: {
                        source: GOALS_EVENT_SOURCES.ADD_GOAL_PANEL,
                        type: GOALS_EVENT_SOURCES.AI.type.HELP_ME_WRITE,
                        goalId: 'NA',
                        purpose: GOALS_EVENT_SOURCES.AI.purpose.Goal_Creation,
                      },
                    });
                    sendClickedOnHelpMeWriteEvent({
                      eventProperties: {
                        source: 'Create Goals',
                      },
                    });
                    dispatch(
                      openTitleAndMetricPanel({
                        isNewOKR: true,
                        isOpenAsChild: isSecondaryPanel,
                        leftPanelDetails: isSecondaryPanel
                          ? { type: SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL, okrDetails }
                          : null,

                        isAlignmentDisabled,
                      })
                    );
                  }}
                  isDisabled={isMeshMavenButtonDisabled}
                />
              </Tooltip>
            </Flex>
          )}
        </Flex>
        <Flex mt="24px" flex="2" flexDir="column">
          <Flex alignItems="center" p="12px" w="100%" h="40px">
            <TextLabel>Other Details</TextLabel>
            <Button
              ml="auto"
              variant="tertiary"
              onClick={() => changeDetailsHandler()}
              sx={{ '.chakra-text': { fontSize: '0.75rem', lineHeight: '1rem' } }}
            >
              Change Details
            </Button>
          </Flex>
          <OtherDetails />
        </Flex>
      </Flex>
      {!isSecondaryPanel && (
        <DrawerFooter
          h="72px"
          backgroundColor="neutral.F5F7F9"
          opacity={showSecondaryPanel ? 0.5 : 1}
          pointerEvents={showSecondaryPanel ? 'none' : 'unset'}
        >
          <OKRCreationFooter
            onCancel={onClose}
            onKeyResultCreation={onKeyResultCreation}
            onObjectiveCreation={onObjectiveCreation}
            renderJustButtons
          />
        </DrawerFooter>
      )}
    </Flex>
  );
};

export default OKRCreationPrimaryPanelV2;
